import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

/**
 * Header cell for the pinned actions column, which is otherwise empty, listing the filters that
 * are currently narrowing the grid and offering to drop any one of them or all of them.
 *
 * AG Grid has no control of its own for this: the Filters tool panel lists every column whether
 * or not it is filtered, and each filter has to be reset on its own. That was survivable while a
 * refresh wiped the filters anyway; now that they persist (POM-6140) a filtered grid can outlive
 * the reason it was filtered, and "why is this row missing" is a harder question than it looks.
 *
 * Reads and writes through the grid api the header is handed, so neither table holds state for it
 * and nothing is passed down. Always rendered, dimmed until it has something to do — a control
 * that appears only once a filter is set is one nobody knows is there.
 */
const AppliedFiltersHeader = ({ api }) => {
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (!api) return undefined;

    const readFilteredColumns = () => {
      const model = api.getFilterModel() ?? {};

      setFilteredColumns(
        Object.keys(model)
          .map((colId) => ({
            colId,
            // Falls back to the column id so a column with no header name is still removable
            // by name rather than missing from the list.
            name: api.getColumnDef(colId)?.headerName || colId,
          }))
          // Alphabetical: the grid's own column order is not recoverable from the filter model,
          // which is keyed in the order the filters happened to be applied.
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
    };

    readFilteredColumns();
    api.addEventListener('filterChanged', readFilteredColumns);

    return () => {
      // The grid can be torn down before this runs, and removing a listener from a destroyed
      // grid throws rather than no-opping.
      if (!api.isDestroyed?.()) api.removeEventListener('filterChanged', readFilteredColumns);
    };
  }, [api]);

  const closePopover = useCallback(() => setAnchorEl(null), []);

  const removeAllFilters = useCallback(() => {
    api?.setFilterModel(null);
    closePopover();
  }, [api, closePopover]);

  const removeOneFilter = useCallback(
    (colId) => {
      const remaining = { ...(api?.getFilterModel() ?? {}) };
      delete remaining[colId];
      api?.setFilterModel(remaining);
      // Closing on the last one only, so removing one of several leaves the list open to remove
      // the next without reopening it.
      if (Object.keys(remaining).length === 0) closePopover();
    },
    [api, closePopover]
  );

  const filterCount = filteredColumns.length;
  const hasFilters = filterCount > 0;
  const label = hasFilters
    ? `${filterCount} filter${filterCount === 1 ? '' : 's'} applied`
    : 'No filters applied';

  return (
    <>
      <Tooltip title={label} placement='bottom' arrow>
        {/* Tooltip needs a real element to attach to while the button is disabled; inline-flex
            so the span does not add the inline baseline gap that knocks it off centre. */}
        <span style={{ display: 'inline-flex' }}>
          <IconButton
            size='small'
            onClick={(event) => setAnchorEl(event.currentTarget)}
            disabled={!hasFilters}
            aria-label={label}
            sx={{
              color: 'primary.contrastText',
              opacity: hasFilters ? 1 : 0.4,
              '&.Mui-disabled': { color: 'primary.contrastText' },
            }}
          >
            <Badge
              badgeContent={filterCount}
              color='error'
              overlap='circular'
              // The header cell is 50px wide; a default badge overflows it and AG Grid clips.
              sx={{ '& .MuiBadge-badge': { height: 14, minWidth: 14, fontSize: 9, px: 0.25 } }}
            >
              <FilterAltIcon fontSize='medium' />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 0.5, minWidth: 240 } }}
      >
        <Box sx={{ px: 2, pt: 1.25, pb: 0.75 }}>
          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontWeight: 600,
            }}
          >
            Filters
          </Typography>
        </Box>
        <Divider />

        <List dense disablePadding>
          {filteredColumns.map(({ colId, name }) => (
            <ListItemButton key={colId} onClick={() => removeOneFilter(colId)} sx={{ py: 0.5 }}>
              <ListItemText
                primary={name}
                primaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
              />
              <CloseIcon sx={{ fontSize: '1rem', color: 'text.disabled', ml: 1.5 }} />
            </ListItemButton>
          ))}
        </List>

        <Divider />
        <ListItemButton onClick={removeAllFilters} sx={{ py: 0.75 }}>
          <ListItemText
            primary='Remove all'
            primaryTypographyProps={{ variant: 'body2', color: 'primary.main' }}
          />
        </ListItemButton>
      </Popover>
    </>
  );
};

AppliedFiltersHeader.propTypes = {
  api: PropTypes.shape({
    getFilterModel: PropTypes.func,
    setFilterModel: PropTypes.func,
    getColumnDef: PropTypes.func,
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
    isDestroyed: PropTypes.func,
  }),
};

AppliedFiltersHeader.defaultProps = { api: null };

export default AppliedFiltersHeader;
